import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { MdDeleteOutline, MdOutlineEmail, MdSchedule } from "react-icons/md";
import { toast } from "react-toastify";
import Btn from "../../shared/button/Btn";
import SmallSpinner from "../../shared/spinners/SmallSpinner";
import {
  getDailyShipmentReportSettings,
  updateDailyShipmentReportSettings,
} from "../../../features/dailyShipmentReport/dailyShipmentReportSlice";

const Container = styled.section`
  width: calc(100% - 350px);
  max-width: 850px;
  color: ${({ theme }) => theme.mainTextColor};

  .title {
    margin: 0 0 6px;
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-lg);
  }

  .description {
    margin: 0;
    color: ${({ theme }) => theme.secondaryTextColor};
    font-size: 13px;
    line-height: 1.55;
  }

  .card {
    margin-top: 20px;
    padding: 22px;
    border: 1px solid ${({ theme }) => theme.mainBorderColor};
    border-radius: 10px;
    background: ${({ theme }) => theme.mainBgColor};
  }

  .switch-row,
  .email-form,
  .email-item,
  .status-line,
  .actions {
    display: flex;
    align-items: center;
  }

  .switch-row {
    justify-content: space-between;
    gap: 20px;
  }

  .switch-copy h3 {
    margin: 0 0 5px;
    font-size: 16px;
  }

  .switch {
    position: relative;
    width: 54px;
    height: 30px;
    flex: 0 0 auto;
    padding: 3px;
    border-radius: 999px;
    background: ${({ $enabled, theme }) =>
      $enabled ? "var(--color-main)" : theme.mainBorderColor};
    transition: background 0.2s ease;
  }

  .switch::after {
    content: "";
    display: block;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
    transform: ${({ $enabled }) =>
      $enabled ? "translateX(24px)" : "translateX(0)"};
    transition: transform 0.2s ease;
  }

  .switch:disabled { cursor: not-allowed; opacity: 0.65; }

  .divider {
    height: 1px;
    margin: 20px 0;
    background: ${({ theme }) => theme.mainBorderColor};
  }

  .section-title {
    margin: 0 0 12px;
    font-size: 14px;
    font-weight: 700;
  }

  .email-form { gap: 10px; }

  .email-input {
    width: 100%;
    height: 40px;
    padding: 10px 14px;
    border: 1px solid ${({ theme }) => theme.mainBorderColor};
    border-radius: 6px;
    color: ${({ theme }) => theme.mainTextColor};
    background: ${({ theme }) => theme.mainBgColor};
  }

  .add-button {
    height: 40px;
    min-width: 110px;
    padding: 0 18px;
    border: 1px solid var(--color-main);
    border-radius: 7px;
    color: #fff;
    background: var(--color-main);
    font-weight: 600;
  }

  .add-button:disabled { cursor: not-allowed; opacity: 0.6; }

  .email-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 14px;
  }

  .email-item {
    justify-content: space-between;
    gap: 10px;
    min-height: 42px;
    padding: 9px 12px;
    border-radius: 7px;
    background: ${({ theme }) => theme.secondaryBgColor};
  }

  .email-value {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    overflow-wrap: anywhere;
  }

  .delete-email {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 30px;
    height: 30px;
    border-radius: 6px;
    color: var(--color-main);
    font-size: 19px;
  }

  .empty {
    margin-top: 14px;
    padding: 18px;
    border: 1px dashed ${({ theme }) => theme.mainBorderColor};
    border-radius: 7px;
    text-align: center;
    color: ${({ theme }) => theme.secondaryTextColor};
    font-size: 13px;
  }

  .list-info {
    margin-top: 8px;
    text-align: right;
    color: ${({ theme }) => theme.secondaryTextColor};
    font-size: 12px;
  }

  .validation-message {
    margin-top: 10px;
    color: var(--color-main);
    font-size: 12px;
    font-weight: 600;
  }

  .status-line {
    gap: 8px;
    margin-top: 18px;
    color: ${({ theme }) => theme.secondaryTextColor};
    font-size: 13px;
  }

  .report-content {
    margin: 10px 0 0 18px;
    color: ${({ theme }) => theme.secondaryTextColor};
    font-size: 12px;
    line-height: 1.65;
  }

  .actions { justify-content: flex-end; margin-top: 22px; }
  .loading, .error-state { padding: 50px 20px; text-align: center; }
  .error-state p { margin: 0 0 16px; }

  @media (max-width: 850px) {
    width: 100%;
    max-width: none;
  }

  @media (max-width: 560px) {
    .card { padding: 16px; }
    .switch-row { align-items: flex-start; }
    .email-form { align-items: stretch; flex-direction: column; }
    .add-button { width: 100%; }
    .actions > * { width: 100%; }
  }
`;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENT_COUNT = 20;

const normalizeEmail = (email) => email.trim().toLowerCase();

const formatReportDate = (dateValue) => {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;
  return date.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const DailyShipmentReportSettings = () => {
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.dailyShipmentReport);
  const {
    isEnabled: savedIsEnabled,
    recipientEmails: savedRecipientEmails,
    lastSentReportDate,
    isLoading,
    isSaving,
    isLoaded,
    error,
  } = settings;
  const [isEnabled, setIsEnabled] = useState(false);
  const [recipientEmails, setRecipientEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");

  useEffect(() => {
    dispatch(getDailyShipmentReportSettings());
  }, [dispatch]);

  useEffect(() => {
    if (!isLoaded) return;
    setIsEnabled(savedIsEnabled);
    setRecipientEmails(savedRecipientEmails);
  }, [isLoaded, savedIsEnabled, savedRecipientEmails]);

  const isDirty = useMemo(
    () =>
      isEnabled !== savedIsEnabled ||
      JSON.stringify(recipientEmails) !== JSON.stringify(savedRecipientEmails),
    [isEnabled, recipientEmails, savedIsEnabled, savedRecipientEmails]
  );

  const handleAddEmail = () => {
    const email = normalizeEmail(emailInput);
    if (!email) return;

    if (!EMAIL_PATTERN.test(email)) {
      toast.error(`Geçersiz e-posta adresi: ${email}`);
      return;
    }

    if (recipientEmails.includes(email)) {
      toast.info("Bu e-posta adresi zaten listede bulunuyor.");
      setEmailInput("");
      return;
    }

    if (recipientEmails.length >= MAX_RECIPIENT_COUNT) {
      toast.error("Bir hesap için en fazla 20 rapor alıcısı tanımlanabilir.");
      return;
    }

    setRecipientEmails((current) => [...current, email]);
    setEmailInput("");
  };

  const handleInputKeyDown = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    handleAddEmail();
  };

  const handleRemoveEmail = (email) => {
    setRecipientEmails((current) => current.filter((item) => item !== email));
  };

  const handleSave = async () => {
    if (isEnabled && recipientEmails.length === 0) {
      toast.error("Raporu açmak için en az bir e-posta adresi girilmelidir.");
      return;
    }

    try {
      await dispatch(
        updateDailyShipmentReportSettings({
          isEnabled,
          recipientEmails,
        })
      ).unwrap();
    } catch (_) {
      // Backend mesajı thunk tarafından toast olarak gösterilir.
    }
  };

  if (isLoading && !isLoaded) {
    return (
      <Container>
        <div className="loading">
          <SmallSpinner size="34px" color="#fff" bgColor="#E85446" />
        </div>
      </Container>
    );
  }

  if (error && !isLoaded) {
    return (
      <Container>
        <div className="error-state">
          <p>{error}</p>
          <Btn
            width="150px"
            bgColor="#E85446"
            color="#fff"
            handleClick={() => dispatch(getDailyShipmentReportSettings())}
          >
            Tekrar Dene
          </Btn>
        </div>
      </Container>
    );
  }

  const formattedLastSentDate = formatReportDate(lastSentReportDate);

  return (
    <Container $enabled={isEnabled}>
      <h2 className="title">Bildirim Ayarları</h2>
      <p className="description">
        Günlük sevkiyat durum raporunun gönderileceği alıcıları yönetin.
      </p>

      <div className="card">
        <div className="switch-row">
          <div className="switch-copy">
            <h3>Günlük Sevkiyat Maili</h3>
            <p className="description">
              Açık olduğunda rapor her gün Türkiye saatiyle 06:00&apos;da gönderilir.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isEnabled}
            aria-label="Günlük sevkiyat mailini aç veya kapat"
            className="switch"
            disabled={isSaving}
            onClick={() => setIsEnabled((current) => !current)}
          />
        </div>

        <div className="divider" />

        <h3 className="section-title">Rapor Alıcıları</h3>
        <div className="email-form">
          <input
            className="email-input"
            type="email"
            value={emailInput}
            placeholder="ornek@firma.com"
            aria-label="Rapor alıcısı e-posta adresi"
            maxLength={254}
            disabled={isSaving || recipientEmails.length >= MAX_RECIPIENT_COUNT}
            onChange={(event) => setEmailInput(event.target.value)}
            onKeyDown={handleInputKeyDown}
          />
          <button
            type="button"
            className="add-button"
            disabled={
              isSaving ||
              !emailInput.trim() ||
              recipientEmails.length >= MAX_RECIPIENT_COUNT
            }
            onClick={handleAddEmail}
          >
            Ekle
          </button>
        </div>

        {recipientEmails.length === 0 ? (
          <div className="empty">Henüz rapor alıcısı eklenmedi.</div>
        ) : (
          <div className="email-list">
            {recipientEmails.map((email) => (
              <div className="email-item" key={email}>
                <div className="email-value">
                  <MdOutlineEmail />
                  <span>{email}</span>
                </div>
                <button
                  type="button"
                  className="delete-email"
                  aria-label={`${email} adresini kaldır`}
                  title="Alıcıyı kaldır"
                  disabled={isSaving}
                  onClick={() => handleRemoveEmail(email)}
                >
                  <MdDeleteOutline />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="list-info">
          {recipientEmails.length}/{MAX_RECIPIENT_COUNT} alıcı
        </div>

        {isEnabled && recipientEmails.length === 0 && (
          <div className="validation-message">
            Raporu etkinleştirmek için en az bir e-posta adresi eklemelisiniz.
          </div>
        )}

        <div className="status-line">
          <MdSchedule />
          <span>
            {formattedLastSentDate
              ? `Son başarılı rapor günü: ${formattedLastSentDate}`
              : "Henüz başarılı bir rapor gönderilmedi."}
          </span>
        </div>

        <div className="divider" />

        <p className="section-title">Raporda yer alan sevkiyatlar</p>
        <ul className="report-content">
          <li>Yüklemede, seferde ve boşaltmada olan sevkiyatlar</li>
          <li>Önceki gün tamamlanan sevkiyatlar</li>
        </ul>

        <div className="actions">
          <Btn
            width="180px"
            bgColor="#E85446"
            color="#fff"
            isDisabled={
              isSaving ||
              !isDirty ||
              (isEnabled && recipientEmails.length === 0)
            }
            handleClick={handleSave}
          >
            {isSaving ? (
              <SmallSpinner size="18px" color="#E85446" bgColor="#fff" />
            ) : (
              "Ayarları Kaydet"
            )}
          </Btn>
        </div>
      </div>
    </Container>
  );
};

export default DailyShipmentReportSettings;
